// Centralized window management system
class WindowManager {
  constructor() {
    this.windows = new Map(); // Map of element -> window info
    this.activeWindow = null;
    this.baseZIndex = 1000;
    this.currentZIndex = this.baseZIndex;
  }
  
  // Register a window with the manager
  register(element, options = {}) {
    const windowInfo = {
      element,
      name: options.name || element.id || 'unnamed',
      zIndex: this.currentZIndex++
    };
    
    this.windows.set(element, windowInfo);
    
    // Set initial z-index
    element.style.zIndex = windowInfo.zIndex;
    
    // Add window classes
    element.classList.add('managed-window');
    if (!this.activeWindow) {
      this.setActive(element);
    } else {
      element.classList.add('window-inactive');
    }
    
    // Add click handler for activation
    this.addActivationHandlers(element);
    
    return windowInfo;
  }
  
  // Unregister a window
  unregister(element) {
    if (this.activeWindow === element) {
      this.activeWindow = null;
    }
    this.windows.delete(element);
  }
  
  // Set the active window
  setActive(element) {
    if (!this.windows.has(element)) return;
    
    // Deactivate previous active window
    if (this.activeWindow && this.activeWindow !== element) {
      this.activeWindow.classList.remove('window-active');
      this.activeWindow.classList.add('window-inactive');
    }
    
    // Activate new window
    this.activeWindow = element;
    element.classList.remove('window-inactive');
    element.classList.add('window-active');
    
    // Bring to front with new z-index
    this.bringToFront(element);
  }
  
  // Bring window to front
  bringToFront(element) {
    const windowInfo = this.windows.get(element);
    if (!windowInfo) return;
    
    // Always increment z-index for the active window
    this.currentZIndex++;
    windowInfo.zIndex = this.currentZIndex;
    element.style.zIndex = this.currentZIndex;
  }
  
  // Add event handlers for window activation
  addActivationHandlers(element) {
    // Use capture phase to ensure we get the event first
    const activateHandler = (e) => {
      // Always activate the window when clicked, regardless of where
      // This ensures immediate response on first click
      this.setActive(element);
    };
    
    // Listen on both capture and bubble phases for immediate response
    element.addEventListener('mousedown', activateHandler, true);
    element.addEventListener('touchstart', activateHandler, { capture: true, passive: true });
    
    // Also handle click events for cases where mousedown might be prevented
    element.addEventListener('click', activateHandler, true);
  }
  
  // Get all windows in z-order
  getWindowsInOrder() {
    return Array.from(this.windows.entries())
      .sort((a, b) => a[1].zIndex - b[1].zIndex)
      .map(([element]) => element);
  }
  
  // === DEVELOPMENT COMMANDS ===
  
  // List all registered windows
  listWindows() {
    const windows = Array.from(this.windows.entries());
    console.group('📋 Registered Windows');
    windows.forEach(([element, info]) => {
      const rect = element.getBoundingClientRect();
      console.log(`${info.name}:`, {
        id: element.id,
        visible: element.style.display !== 'none',
        zIndex: info.zIndex,
        position: `${rect.left},${rect.top}`,
        size: `${rect.width}x${rect.height}`,
        active: element === this.activeWindow
      });
    });
    console.groupEnd();
    return windows.length;
  }
  
  // Close all windows
  closeAll() {
    this.windows.forEach((info, element) => {
      element.style.display = 'none';
      console.log(`Closed ${info.name}`);
    });
  }
  
  // Show all windows (for debugging overlap issues)
  showAll() {
    this.windows.forEach((info, element) => {
      element.style.display = 'flex';
      console.log(`Shown ${info.name}`);
    });
  }
  
  // Arrange windows in a grid (for debugging)
  arrangeGrid(columns = 2, padding = 20) {
    const windows = Array.from(this.windows.entries());
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const windowWidth = (viewportWidth - padding * (columns + 1)) / columns;
    const windowHeight = 400; // Fixed height for grid
    
    windows.forEach(([element, info], index) => {
      const col = index % columns;
      const row = Math.floor(index / columns);
      const left = padding + col * (windowWidth + padding);
      const top = 60 + row * (windowHeight + padding); // 60px for safe area
      
      element.style.left = `${left}px`;
      element.style.top = `${top}px`;
      element.style.width = `${windowWidth}px`;
      if (element.style.height !== 'auto') {
        element.style.height = `${windowHeight}px`;
      }
      element.style.display = 'flex';
      
      console.log(`Positioned ${info.name} at ${left},${top}`);
    });
  }
  
  // Stack windows with offset (cascade)
  cascade(offsetX = 30, offsetY = 30) {
    const windows = this.getWindowsInOrder();
    let currentX = 100;
    let currentY = 100;
    
    windows.forEach(element => {
      element.style.left = `${currentX}px`;
      element.style.top = `${currentY}px`;
      element.style.display = 'flex';
      
      currentX += offsetX;
      currentY += offsetY;
      
      // Wrap if getting too close to edge
      if (currentX > window.innerWidth - 500) {
        currentX = 100;
        currentY = 100;
      }
    });
    console.log('Cascaded all windows');
  }
  
  // Center all windows (for testing centering logic)
  centerAll() {
    this.windows.forEach((info, element) => {
      const rect = element.getBoundingClientRect();
      const left = (window.innerWidth - rect.width) / 2;
      const top = Math.max(50, (window.innerHeight - rect.height) / 2);
      
      element.style.left = `${left}px`;
      element.style.top = `${top}px`;
      console.log(`Centered ${info.name}`);
    });
  }
  
  // Reset all window positions (clear saved positions)
  resetPositions() {
    // Clear all localStorage position data
    const keys = Object.keys(localStorage);
    const positionKeys = keys.filter(key => key.includes('_position') || key.includes('_size'));
    positionKeys.forEach(key => {
      localStorage.removeItem(key);
      console.log(`Removed ${key}`);
    });
    console.log(`Cleared ${positionKeys.length} position/size entries`);
    console.log('Reload the page to see windows at default positions');
  }
  
  // Debug window info
  debug(elementOrId) {
    let element;
    if (typeof elementOrId === 'string') {
      element = document.getElementById(elementOrId);
    } else {
      element = elementOrId;
    }
    
    const info = this.windows.get(element);
    if (!info) {
      console.error('Window not registered:', elementOrId);
      return;
    }
    
    const rect = element.getBoundingClientRect();
    const computed = window.getComputedStyle(element);
    
    console.group(`🔍 Debug: ${info.name}`);
    console.log('Registration:', info);
    console.log('Position:', {
      left: computed.left,
      top: computed.top,
      transform: computed.transform
    });
    console.log('Bounds:', {
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height
    });
    console.log('Visibility:', {
      display: computed.display,
      visibility: computed.visibility,
      opacity: computed.opacity
    });
    console.log('Classes:', element.className);
    console.groupEnd();
  }
}

// Create singleton instance
export const windowManager = new WindowManager();

// Make it globally accessible for debugging
window.windowManager = windowManager;

// Add convenient global commands for development
window.wm = {
  list: () => windowManager.listWindows(),
  closeAll: () => windowManager.closeAll(),
  showAll: () => windowManager.showAll(),
  grid: (cols) => windowManager.arrangeGrid(cols),
  cascade: () => windowManager.cascade(),
  center: () => windowManager.centerAll(),
  reset: () => windowManager.resetPositions(),
  debug: (id) => windowManager.debug(id)
};

console.log('🪟 Window Manager Dev Commands:');
console.log('  wm.list()     - List all windows');
console.log('  wm.closeAll() - Close all windows');
console.log('  wm.showAll()  - Show all windows');
console.log('  wm.grid(2)    - Arrange in grid');
console.log('  wm.cascade()  - Cascade windows');
console.log('  wm.center()   - Center all windows');
console.log('  wm.reset()    - Clear saved positions');
console.log('  wm.debug(id)  - Debug window info');